import type { SelectChangeEvent } from '@mui/material';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import _ from 'lodash';
import type { FC } from 'react';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import type { VariantType } from '@components/StatusPill/StatusPill';
import StatusPill from '@components/StatusPill/StatusPill';

import { useGetStatuses } from '@hooks/api/status';

import type { StatusType } from 'generated/types';

interface StatusDropdownProps {
  /**
   * Entity (type) of status
   */
  model: StatusType;
  /**
   * Name attribute of the input
   */
  name?: string;
  /**
   * Callback fired when a status is selected
   */
  onChange?: (event: SelectChangeEvent, child: React.ReactNode) => void;

  /**
   * Input value of the status dropdown
   */
  value?: string;

  /**
   * Label for the dropdown
   */
  label?: string;
  /**
   * Variant of the status dropdown
   */
  variant?: VariantType;
  /**
   * Base loading state
   */
  loading?: boolean;
}

const messages = defineMessages({
  select: {
    id: 'GLOBAL.selectFrom',
    defaultMessage: 'Select...',
  },
});

const StatusDropdown: FC<StatusDropdownProps> = ({
  model,
  name,
  onChange,
  value,
  label,
  variant = 'primary',
  loading: baseLoading = false,
}) => {
  const [labelId] = useState(_.uniqueId('status-dropdown-'));
  const { formatMessage } = useIntl();

  const { data: statusesData, loading: statusesLoading } = useGetStatuses({
    model,
  });
  const loading = baseLoading || statusesLoading;
  const statuses = statusesData ?? [];

  const handleChange = (event: SelectChangeEvent, child: React.ReactNode) => {
    onChange?.(event, child);
  };

  const preventPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
  };

  return (
    <FormControl size="small" sx={{ width: 'fit-content', minWidth: 160 }}>
      {label && <InputLabel id={labelId}>{label}</InputLabel>}
      <ContentLoader loading={loading}>
        <Select
          displayEmpty
          labelId={labelId}
          name={name ?? ''}
          onChange={handleChange}
          onClick={preventPropagation}
          value={value}
        >
          <MenuItem value="">{formatMessage(messages.select)}</MenuItem>
          {statuses.map(({ color, private_name, status_id }, index) => (
            <MenuItem key={`dropdown-item-${index}`} value={status_id}>
              <StatusPill name={private_name} color={color} variant={variant} />
            </MenuItem>
          ))}
        </Select>
      </ContentLoader>
    </FormControl>
  );
};

export default StatusDropdown;
